// src/utils/validateSurvey.ts
import type { SurveyQuestion } from "../types/defaultTypes";

export type ValidationError = {
  survey: {
    title?: string;
    description?: string;
    surveyTrigger?: string;
    questions?: string;
  };
  questions: {
    id: string;
    questionError?: string;
    optionErrors?: { id: string; error: string }[];
    settings?: {
      minChars?: string;
      maxChars?: string;
    };
  }[];
  organization: string;
  course: string;
};

export const validateSurvey = (
  selectedOrg: number,
  selectedCourse: number,
  surveyInfo: {
    title: string | null | undefined;
    description: string | null | undefined;
    surveyTrigger: string | number | null | undefined;
  },
  questions: SurveyQuestion[]
): ValidationError => {
  const errors: ValidationError = {
    survey: {},
    questions: [],
    organization: "",
    course: "",
  };

  // Validate organization and course
  if (!selectedOrg || selectedOrg === 0) errors.organization = "Select Organization";
  if (!selectedCourse || selectedCourse === 0) errors.course = "Select Course";

  // Safe string conversion for validation
  if (!String(surveyInfo.title || "").trim()) errors.survey.title = "Survey title cannot be empty";
  if (!String(surveyInfo.description || "").trim()) errors.survey.description = "Survey description cannot be empty";
  if (!String(surveyInfo.surveyTrigger || "").trim()) errors.survey.surveyTrigger = "Survey delay cannot be empty";
  if (!questions || questions.length === 0) errors.survey.questions = "At least one question is required";

  // Validate each question
  questions.forEach((q) => {
    let qError: string | undefined;
    let optionErrors: { id: string; error: string }[] = [];
    let settings = { minChars: "", maxChars: "" };

    if (!String(q.text || "").trim()) qError = "Question text cannot be empty";

    if (q.type === "single-choice" || q.type === "multi-choice") {
      if (!q.options || q.options.length === 0) {
        qError = "Choice questions must have at least one option";
      } else {
        q.options.forEach((opt) => {
          if (!String(opt.answer || "").trim()) {
            optionErrors.push({ id: opt.id, error: "Option cannot be empty" });
          }
        });

        if (!q.correctAnswers || q.correctAnswers.length === 0) {
          qError = qError ?? "Select at least one correct answer";
        }
      }
    }

    if (q.type === "open-ended") {
      if (!q?.settings?.minChars || q?.settings?.minChars === 0)
        settings.minChars = "Enter a minimal number of characters";
      if (!q?.settings?.maxChars || q?.settings?.maxChars === 0)
        settings.maxChars = "Enter a maximum number of characters";
    }

    if (qError || optionErrors.length > 0 || settings.minChars || settings.maxChars) {
      errors.questions.push({ id: q.id, questionError: qError, optionErrors, settings });
    }
  });

  return errors;
};
